import { notificationsApi } from "../api/notifications";

/**
 * Web Push client (UIUX §8.3). Feature-detects three independent
 * layers and degrades honestly at each:
 *   1. browser support (service worker + PushManager + Notification),
 *   2. server provisioning (VAPID keys — `GET /notifications/push/config`),
 *   3. user permission (never prompted on load; only from the settings
 *      toggle).
 */

export function browserSupportsPush(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Register the built service worker. No-op in dev (vite doesn't serve it). */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (!("serviceWorker" in navigator)) return;

  // Auto-recover an already-open tab after a deploy. The worker uses
  // skipWaiting + clients.claim and deletes the previous shell cache on
  // activate, so once a newly-deployed worker takes control the running
  // tab is on STALE code whose chunks may have just been purged — leaving
  // old UI (or a blank surface when a stale lazy import 404s) until a
  // manual reload. Reload once when control actually changes.
  //
  // Guarded two ways against reload loops: only when this page STARTED
  // controlled (`controller` set at load = a real update, not the first
  // install), and only once via the `reloading` latch.
  const hadController = Boolean(navigator.serviceWorker.controller);
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || reloading) return;
    reloading = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.warn("[pwa] service worker registration failed", err);
    });
  });
}

/** The device's current subscription, if any. */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!browserSupportsPush()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

/**
 * Subscribe this device and persist the subscription server-side.
 * Throws with a human-readable message when permission is denied or
 * the worker isn't active — the settings row surfaces it.
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked in your browser settings."
        : "Notification permission was not granted.",
    );
  }
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    throw new Error("The app's service worker isn't installed yet — reload and try again.");
  }
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error("The browser returned an incomplete push subscription.");
  }
  await notificationsApi.subscribePush({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
}

/** Unsubscribe this device locally and delete the server row. */
export async function unsubscribeFromPush(): Promise<void> {
  const subscription = await getPushSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await notificationsApi.unsubscribePush(endpoint);
}

/** VAPID keys are URL-safe base64; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}
